import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { create, show, useModal } from "@ebay/nice-modal-react";

import FlashContext from "components/FlashMessages/FlashContext";
import ConfirmationModal from "ds/components/ConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useTypedContext from "hooks/useTypedContext";
import useDownloadApiKeyConfig from "shared/ApiKey/useDownloadApiKeyConfig";
import { ApiKey } from "types/generated";

import { RESET_API_KEY } from "./gql";

type ResetConfirmationProps = {
  id: ApiKey["id"];
  name: ApiKey["name"];
};

const ResetConfirmation = create(function ResetConfirmation({ id, name }: ResetConfirmationProps) {
  const modal = useModal();

  const { onError, reportError } = useTypedContext(FlashContext);
  const downloadApiKeyConfig = useDownloadApiKeyConfig();

  const [resetApiKey, { loading }] = useMutation(RESET_API_KEY, {
    fetchPolicy: "no-cache",
  });

  const onConfirm = useCallback(async () => {
    try {
      const { data } = await resetApiKey({ variables: { id } });

      if (!data?.apiKeyReset) {
        return reportError({
          message: "API key could not be reset. It may have been deleted in the meantime.",
        });
      }

      const downloaded = downloadApiKeyConfig({
        ...data.apiKeyReset,
        successMessage: `Your new credentials file is being downloaded for Key ID: ${data.apiKeyReset.id}`,
      });

      if (downloaded) {
        modal.resolve();
        modal.hide();
      }
    } catch (error) {
      onError(error);
    }
  }, [resetApiKey, id, reportError, downloadApiKeyConfig, modal, onError]);

  return (
    <ConfirmationModal
      title="Reset API key"
      confirmationButtonLabel="Reset"
      onConfirm={onConfirm}
      isLoading={loading}
      isDismissable={!loading}
    >
      <ConfirmationModalMessage>
        Resetting <strong>{name}</strong> invalidates its current secret immediately and ends any
        active sessions using it. You&apos;ll get a new credentials file to roll out.
      </ConfirmationModalMessage>
    </ConfirmationModal>
  );
});

export const showResetConfirmation = (props: ResetConfirmationProps) =>
  show(ResetConfirmation, props);
